import {
  EventDeletableStatus,
  EventSeriesSortKeys,
  EventSortKeys,
} from '@app/graphql/types-and-hooks';
import {
  defaultState as defaultOrganizationProfileState,
  State as OrganizationProfileState,
} from '@app/routes/organization/routes/manage/domain';
import {
  defaultState as defaultSpacesState,
  State as SpacesState,
} from '@app/routes/organization/routes/manage/routes/spaces/domain';
import {
  defaultState as defaultActiveMembersState,
  State as ActiveMembersState,
} from '@app/routes/organization/routes/manage/routes/team/routes/active-member/domain';
import {
  defaultState as defaultPendingInvitesState,
  State as PendingInvitesState,
} from '@app/routes/organization/routes/manage/routes/team/routes/pending-invites/domain';
import { AuthenticationState, TransactionState } from '@app/store/domain';

import {
  defaultState as defaultManageTeamState,
  State as ManageTeamState,
} from './routes/manage/routes/team/domain';
import {
  defaultState as defaultEventTeamAccessState,
  State as EventTeamAccessState,
} from './routes/manage-event/domain';

export type EventStatus = 'ended' | 'live' | 'upcoming' | 'draft' | 'building';

export type EventId = string;
export type RecordingId = string;

export type EventFilters = {
  searchText: string;
  eventStatus: EventStatus[];
};

export type SeriesFilters = {
  searchText: string;
};

export type Event = {
  id: string;
  name: string;
  slug: string;
  registrationsCount: number | null;
  status: string;
  description: string | null;
  timeStart: string;
  timeEnd: string;
  deletable: EventDeletableStatus | null;
  image: {
    url: string | null;
  };
  expired: boolean;
  isLite: boolean;
  venueType: string;
  eventSeriesCount: number;
  eventSeriesNames: string[];
  eventSeries: {
    edges: {
      node: {
        id: string;
        eventsCount: number;
        name: string;
      };
    }[];
  };
};

export type PageInfo = {
  hasNextPage: boolean;
  endCursor: string | null;
};

export type EventSeries = {
  id: string;
  name: string;
  eventsCount: number;
  registrants: number;
  status: string;
  timeStart: string;
  timeEnd: string;
  seriesImage: {
    url: string | null;
  }[];
  allowMultiEventRegistration: boolean;
};

export type OrgSpace = {
  externalId: string;
  name: string;
};

export type State = {
  authentication: AuthenticationState;
  organizationInfo: {
    name: string;
    about: string | null;
    email: string;
    facebookUrl: string | null;
    linkedinUrl: string | null;
    twitterUrl: string | null;
    websiteUrl: string | null;
    features: {
      eventsLite: boolean;
      eventsPro: boolean;
      onsiteHybridEvents: boolean;
      appStore: boolean;
    };
    paymentPlan: {
      planName: string;
      planNameLocale: string;
      downgradedToFree: boolean;
      endsAt: string;
      isAdvanced: boolean;
    };
    error: {
      message: string;
    };
    image: string;
    smallImage: string;
    largeImage: string;
    createdAt: string;
    standaloneStudio: null | {
      name: string;
      uuid: string;
      eventSlug: string;
    };
    settings?: {
      disableAiFeatures: boolean;
    };
  } & TransactionState;
  organizationEvents: {
    [key: string]: Event;
  };
  organizationEventsHasRecordings: {
    hasRecordingsMap: Record<string, boolean>;
    error?: {
      message: string;
    };
  } & TransactionState;
  organizationLatestEndedEvents: {
    items: Record<string, boolean>;
    error: {
      message: string;
    };
  } & TransactionState;
  organizationLatestEndedEventsRecordings: {
    recordingsMap: Record<EventId, RecordingId>;
    error?: {
      message: string;
    };
  } & TransactionState;
  organizationSeries: {
    [key: string]: EventSeries;
  };
  events: {
    filters: EventFilters;
    items: string[];
    pageInfo: {
      hasNextPage: boolean;
      endCursor: string | null;
    };
    features: {
      eventsLite: boolean;
      eventsPro: boolean;
    };
    error: {
      message: string;
    };
    sort: {
      sortField: EventSortKeys;
      reverseSort: boolean;
    };
    loadFirstPage: boolean;
  } & TransactionState;
  series: {
    expandedSeriesId: string;
    filters: SeriesFilters;
    items: string[];
    pageInfo: {
      hasNextPage: boolean;
      endCursor: string | null;
    };
    error: {
      message: string;
    };
    sort: {
      sortField: EventSeriesSortKeys;
      reverseSort: boolean;
    };
    loadFirstPage: boolean;
  } & TransactionState;
  seriesEvents: {
    [key: string]: {
      items: Event[];
    } & TransactionState;
  };
  addEventsToSeries: {
    searchText: string;
    events: Event[];
    pageInfo: {
      hasNextPage: boolean;
      endCursor: string | null;
    };
    pinSeries: string;
    addedEventIds: string[];
  } & TransactionState;
  createSeriesModal: {
    eventIds: string[];
    eventSearchText: string;
    pageInfo: {
      hasNextPage: boolean;
      endCursor: string | null;
    };
  } & TransactionState;
  addSeriesToEvent: {
    searchText: string;
    seriesList: Partial<EventSeries>[];
    pageInfo: {
      hasNextPage: boolean;
      endCursor: string | null;
    };
    pinEvent: string;
  } & TransactionState;
  showEventDuplicateNotAllowedModal: boolean;
  profileUpdate: OrganizationProfileState;
  teamActiveMembers: ActiveMembersState;
  teamPendingInvites: PendingInvitesState;
  manageTeam: ManageTeamState;
  eventTeamAccess: EventTeamAccessState;
  spaces: SpacesState;
  orgSpaces: {
    spaces: OrgSpace[];
    pageInfo: PageInfo;
  } & TransactionState;
  spacesFeatureFlagEnabled: boolean;
  currentSpace: {
    id: string;
    name: string;
  };
  initialSpaceIdResolved: boolean;
  invalidSpaceAccessModal: {
    isOpen: boolean;
    previousSpaceName: string;
    newSpaceName: string;
  };
};

export const defaultState: State = {
  organizationInfo: {
    status: 'idle',
    name: '',
    about: null,
    email: '',
    twitterUrl: null,
    linkedinUrl: null,
    facebookUrl: null,
    websiteUrl: null,
    features: {
      eventsLite: false,
      eventsPro: false,
      onsiteHybridEvents: false,
      appStore: false,
    },
    paymentPlan: {
      planName: '',
      planNameLocale: '',
      downgradedToFree: false,
      endsAt: '',
      isAdvanced: false,
    },
    error: {
      message: '',
    },
    image: '',
    smallImage: '',
    largeImage: '',
    createdAt: '',
    standaloneStudio: null,
    settings: {
      disableAiFeatures: false,
    },
  },
  organizationEvents: {},
  organizationEventsHasRecordings: {
    hasRecordingsMap: {},
    status: 'idle',
  },
  organizationLatestEndedEvents: {
    items: {},
    status: 'idle',
    error: {
      message: '',
    },
  },
  organizationLatestEndedEventsRecordings: {
    recordingsMap: {},
    status: 'idle',
  },
  organizationSeries: {},
  authentication: {
    status: 'idle',
    contentApiAuthToken: null,
    contentApiEndpoint: null,
  },
  events: {
    status: 'idle',
    filters: {
      searchText: '',
      eventStatus: [],
    },
    items: [],
    pageInfo: {
      hasNextPage: false,
      endCursor: null,
    },
    features: {
      eventsLite: false,
      eventsPro: false,
    },
    error: {
      message: '',
    },
    sort: {
      sortField: EventSortKeys.TimeStart,
      reverseSort: true,
    },
    loadFirstPage: true,
  },
  series: {
    expandedSeriesId: '',
    status: 'idle',
    filters: {
      searchText: '',
    },
    items: [],
    pageInfo: {
      hasNextPage: false,
      endCursor: null,
    },
    error: {
      message: '',
    },
    sort: {
      sortField: EventSeriesSortKeys.Name,
      reverseSort: false,
    },
    loadFirstPage: true,
  },
  seriesEvents: {},
  addEventsToSeries: {
    searchText: '',
    events: [],
    pageInfo: {
      hasNextPage: false,
      endCursor: null,
    },
    pinSeries: '',
    addedEventIds: [],
    status: 'idle',
  },
  createSeriesModal: {
    eventIds: [],
    eventSearchText: '',
    pageInfo: {
      hasNextPage: false,
      endCursor: null,
    },
    status: 'idle',
  },
  addSeriesToEvent: {
    seriesList: [],
    pageInfo: {
      hasNextPage: false,
      endCursor: null,
    },
    status: 'idle',
    searchText: '',
    pinEvent: '',
  },
  showEventDuplicateNotAllowedModal: false,
  profileUpdate: defaultOrganizationProfileState,
  teamActiveMembers: defaultActiveMembersState,
  teamPendingInvites: defaultPendingInvitesState,
  manageTeam: defaultManageTeamState,
  eventTeamAccess: defaultEventTeamAccessState,
  spaces: defaultSpacesState,
  orgSpaces: {
    status: 'idle',
    spaces: [],
    pageInfo: {
      hasNextPage: false,
      endCursor: null,
    },
  },
  currentSpace: {
    id: '',
    name: '',
  },
  initialSpaceIdResolved: false,
  spacesFeatureFlagEnabled: false,
  invalidSpaceAccessModal: {
    isOpen: false,
    previousSpaceName: '',
    newSpaceName: '',
  },
};

export type EventHasRecordingsResponse = {
  event_id: string;
  has_recordings: boolean;
}[];

export type EventLongestRecordingResponse = {
  event_id: string;
  longest_recording_id: string;
}[];
